/**
 * Artemis panels, ported from loader-infopanels.js (blog every 5 min with a
 * "new" badge, NASA image search with 12 thumbnails and a lightbox swapping
 * /thumb/ for /medium/, aggregated space weather every 10 min with the crew
 * risk rule), loader-timeline.js (phases from artemis2-timeline.json, the
 * current phase from the sim clock, antennas with enabledPhases shown only in
 * those phases) and loader-telemetry.js (Orion telemetry at up to 4 Hz from
 * the ephemeris samples the runtime holds; MET is relative to the timeline
 * launchDate, fixing the hard-coded launch time the audit flagged).
 */
import { loaderApi } from '../data/api';
import { loadArtemisTimeline } from '../data/staticData';
import type { Antenna, MissionPhase, MissionTimeline } from '../types';
import { distance, magnitude, type EphemerisTrack } from './ephemeris';
import { useAwareness, type ArtemisSpaceWeather, type BlogPost, type NasaImage, type OrionTelemetry } from './store';
import { errorMessage, parseMs, startPolling } from './util';

export const BLOG_REFRESH_MS = 5 * 60 * 1000;
export const ARTEMIS_SPACE_WEATHER_REFRESH_MS = 10 * 60 * 1000;
export const NASA_IMAGES_URL = 'https://images-api.nasa.gov/search?q=artemis+II&media_type=image&year_start=2026';
export const IMAGE_LIMIT = 12;
export const TELEMETRY_TICK_MS = 250;
export const SPEED_OF_LIGHT_KM_S = 299_792.458;

// ------------------------------------------------------------------- blog

/** Posts newer than the last-seen date; none before a first render has set it. */
export function countNewPosts(posts: BlogPost[], lastSeenMs: number | null): number {
  if (lastSeenMs === null) return 0;
  let n = 0;
  for (const p of posts) {
    const ms = parseMs(p.date);
    if (ms !== null && ms > lastSeenMs) n++;
  }
  return n;
}

/** The original fixed the last-seen date once, from the first post of the first successful render. */
export function nextLastSeen(posts: BlogPost[], lastSeenMs: number | null): number | null {
  if (lastSeenMs !== null) return lastSeenMs;
  if (posts.length === 0) return null;
  return parseMs(posts[0].date);
}

export async function refreshArtemisBlog(): Promise<void> {
  const store = useAwareness.getState();
  try {
    const data = await loaderApi.artemisBlog<{ posts?: BlogPost[] }>();
    const posts = Array.isArray(data?.posts) ? data.posts : [];
    const blog = useAwareness.getState().artemis.blog;
    store.setArtemis({
      blog: { data: posts, error: null, newCount: countNewPosts(posts, blog.lastSeenMs), lastSeenMs: nextLastSeen(posts, blog.lastSeenMs) },
    });
  } catch (err) {
    console.warn('[artemis] blog feed unavailable:', err);
    const blog = useAwareness.getState().artemis.blog;
    store.setArtemis({ blog: { ...blog, error: errorMessage(err) } });
  }
}

export function startArtemisBlog(): () => void {
  return startPolling(() => {
    void refreshArtemisBlog();
  }, BLOG_REFRESH_MS);
}

// ----------------------------------------------------------------- images

export interface NasaImageItem {
  links?: { href: string }[];
  data?: { title?: string }[];
}

export function summariseImages(items: NasaImageItem[], limit: number = IMAGE_LIMIT): NasaImage[] {
  const out: NasaImage[] = [];
  for (const item of items.slice(0, limit)) {
    const thumb = item.links && item.links.length > 0 ? item.links[0].href : '';
    if (!thumb) continue;
    out.push({ thumb, title: item.data && item.data[0] ? item.data[0].title || '' : '' });
  }
  return out;
}

/** The lightbox image: NASA's medium rendition sits next to the thumbnail. */
export function mediumImageUrl(thumbUrl: string): string {
  return thumbUrl.replace('/thumb/', '/medium/');
}

export async function loadArtemisImages(fetchJson: (url: string) => Promise<unknown> = defaultFetchJson): Promise<void> {
  const store = useAwareness.getState();
  try {
    const data = (await fetchJson(NASA_IMAGES_URL)) as { collection?: { items?: NasaImageItem[] } };
    const items = data?.collection?.items;
    if (!Array.isArray(items)) throw new Error('NASA image search returned no collection');
    store.setArtemis({ images: { data: summariseImages(items), error: null } });
  } catch (err) {
    console.warn('[artemis] NASA images unavailable:', err);
    store.setArtemis({ images: { data: null, error: errorMessage(err) } });
  }
}

async function defaultFetchJson(url: string): Promise<unknown> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${url} returned HTTP ${res.status}`);
  return res.json();
}

// ---------------------------------------------------------- space weather

export type KpClass = 'quiet' | 'active' | 'storm';

/** Quiet, Active at Kp >= 4, Storm at Kp >= 6 (the aggregated panel's thresholds). */
export function kpClass(kp: number | null | undefined): KpClass {
  if (kp === null || kp === undefined) return 'quiet';
  if (kp >= 6) return 'storm';
  if (kp >= 4) return 'active';
  return 'quiet';
}

export function kpClassLabel(cls: KpClass): string {
  return cls === 'storm' ? 'Storm' : cls === 'active' ? 'Active' : 'Quiet';
}

export type RiskLevel = 'Low' | 'Moderate' | 'Elevated';

/** Crew risk: Elevated at Kp >= 6 or any X-class flare or CME alert; Moderate at Kp >= 4; else Low. */
export function riskLevel(data: Pick<ArtemisSpaceWeather, 'kpIndex' | 'solarFlares' | 'cmeAlerts'>): RiskLevel {
  const kp = data.kpIndex?.current ?? null;
  const flares = data.solarFlares || [];
  const cmes = data.cmeAlerts || [];
  const alertCount = flares.filter((f) => f.classType && f.classType.charAt(0) === 'X').length + cmes.length;
  if ((kp !== null && kp >= 6) || alertCount > 0) return 'Elevated';
  if (kp !== null && kp >= 4) return 'Moderate';
  return 'Low';
}

export async function refreshArtemisSpaceWeather(): Promise<void> {
  const store = useAwareness.getState();
  try {
    const data = await loaderApi.spaceWeather<ArtemisSpaceWeather>();
    if (!data) throw new Error('space-weather returned nothing');
    store.setArtemis({ spaceWeather: { data, error: null } });
  } catch (err) {
    console.warn('[artemis] space weather unavailable:', err);
    const previous = useAwareness.getState().artemis.spaceWeather;
    store.setArtemis({ spaceWeather: { data: previous.data, error: errorMessage(err) } });
  }
}

export function startArtemisSpaceWeather(): () => void {
  return startPolling(() => {
    void refreshArtemisSpaceWeather();
  }, ARTEMIS_SPACE_WEATHER_REFRESH_MS);
}

// --------------------------------------------------------------- timeline

/** The phase containing t ([start, end)); the first phase before the mission, the last after it; null without phases. */
export function currentPhase(phases: readonly MissionPhase[], tMs: number): MissionPhase | null {
  if (!phases || phases.length === 0) return null;
  for (const p of phases) {
    const start = new Date(p.start).getTime();
    const end = new Date(p.end).getTime();
    if (tMs >= start && tMs < end) return p;
  }
  if (tMs < new Date(phases[0].start).getTime()) return phases[0];
  return phases[phases.length - 1];
}

export interface PhaseSegment {
  phase: MissionPhase;
  leftPct: number;
  widthPct: number;
}

/** Segment geometry for the phase bar: position proportional to time over launch to splashdown, minimum width 0.5%. */
export function phaseSegments(timeline: MissionTimeline): PhaseSegment[] {
  const phases = timeline.phases;
  if (phases.length === 0) return [];
  const startMs = new Date(timeline.launchDate || phases[0].start).getTime();
  const endMs = new Date(timeline.splashdownDate || phases[phases.length - 1].end).getTime();
  const totalMs = endMs - startMs;
  if (!(totalMs > 0)) return [];
  return phases.map((phase) => {
    const pStart = new Date(phase.start).getTime();
    const pEnd = new Date(phase.end).getTime();
    return {
      phase,
      leftPct: Math.max(0, ((pStart - startMs) / totalMs) * 100),
      widthPct: Math.max(0.5, ((pEnd - pStart) / totalMs) * 100),
    };
  });
}

/** Antennas with enabledPhases are shown only in those phases; the rest are left alone. */
export function phaseAntennaVisibility(antennas: readonly Antenna[], phaseId: string): { id: string; visible: boolean }[] {
  const out: { id: string; visible: boolean }[] = [];
  for (const a of antennas) {
    if (!a.enabledPhases) continue;
    out.push({ id: a.id, visible: a.enabledPhases.includes(phaseId) });
  }
  return out;
}

export async function loadTimeline(): Promise<MissionTimeline | null> {
  try {
    const timeline = await loadArtemisTimeline();
    if (!timeline || !Array.isArray(timeline.phases)) throw new Error('timeline JSON has no phases');
    useAwareness.getState().setArtemis({ timeline });
    return timeline;
  } catch (err) {
    console.warn('[artemis] mission timeline unavailable:', err);
    useAwareness.getState().setArtemis({ timeline: null, currentPhaseId: null });
    return null;
  }
}

// -------------------------------------------------------------- telemetry

/** T+DD:HH:MM:SS, T- before launch. */
export function formatMet(ms: number): string {
  const prefix = ms >= 0 ? 'T+' : 'T-';
  let rest = Math.abs(ms);
  const days = Math.floor(rest / 86_400_000);
  rest %= 86_400_000;
  const hrs = Math.floor(rest / 3_600_000);
  rest %= 3_600_000;
  const mins = Math.floor(rest / 60_000);
  rest %= 60_000;
  const secs = Math.floor(rest / 1000);
  const p2 = (n: number) => String(n).padStart(2, '0');
  return `${prefix}${p2(days)}:${p2(hrs)}:${p2(mins)}:${p2(secs)}`;
}

export function formatDistanceKm(km: number): string {
  return Math.round(km).toLocaleString('en') + ' km';
}

export function formatDelay(sec: number): string {
  return sec > 60 ? (sec / 60).toFixed(1) + ' min' : sec.toFixed(2) + ' s';
}

/**
 * Telemetry at sim time tMs: distances from the Earth centre and the Moon,
 * speed by a 1 s finite difference of the interpolated inertial position,
 * one-way light time and the phase. Null when the mission track does not
 * cover tMs; the Moon distance is null without moon samples.
 */
export function computeTelemetry(
  track: EphemerisTrack,
  moonTrack: EphemerisTrack | null,
  tMs: number,
  launchMs: number,
  phases: readonly MissionPhase[],
): OrionTelemetry | null {
  const pos = track.positionAt(tMs);
  if (!pos) return null;
  const distEarthKm = magnitude(pos);
  const moonPos = moonTrack ? moonTrack.positionAt(tMs) : null;
  const distMoonKm = moonPos ? distance(pos, moonPos) : null;
  const future = track.positionAt(tMs + 1000);
  const velocityKmS = future ? distance(future, pos) : null;
  return {
    metMs: tMs - launchMs,
    distEarthKm,
    distMoonKm,
    velocityKmS,
    oneWayDelaySec: distEarthKm / SPEED_OF_LIGHT_KM_S,
    phaseName: currentPhase(phases, tMs)?.name ?? null,
    atMs: tMs,
  };
}
